import React from 'react';

const NotificationToast = ({ notifications, onRemove }) => {
  const getNotificationIcon = (notification) => {
    if (notification.type === 'followup') return '📅';
    if (notification.type === 'success') return '✅';
    if (notification.type === 'error') return '❌';
    return '🎯';
  };

  const getNotificationColor = (notification) => {
    if (notification.type === 'followup') return 'purple';
    if (notification.type === 'success') return 'green';
    if (notification.type === 'error') return 'red';
    return 'blue';
  };

  const getNotificationTitle = (notification) => {
    if (notification.type === 'followup') return 'Follow-Up Scheduled!';
    if (notification.type === 'success') return 'Success!';
    if (notification.type === 'error') return 'Error!';
    return 'New Lead Captured!';
  };

  const borderColorMap = {
    purple: '#8b5cf6',
    green:  '#22c55e',
    red:    '#ef4444',
    blue:   '#3b82f6'
  };

  const bgColorMap = {
    purple: '#f5f3ff',
    green:  '#f0fdf4',
    red:    '#fef2f2',
    blue:   '#eff6ff'
  };

  const textColorMap = {
    purple: '#7c3aed',
    green:  '#16a34a',
    red:    '#dc2626',
    blue:   '#2563eb'
  };

  return (
    <>
      <style>{`
        @keyframes slideInToast {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes shrink {
          from { width: 100%; }
          to   { width: 0%; }
        }

        /* ── Desktop: fixed top-right ── */
        .toast-container {
          position: fixed;
          top: 80px;
          right: 16px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 10px;
          width: 360px;
          max-width: calc(100vw - 32px);
          pointer-events: none;
        }
        .toast-item {
          pointer-events: all;
          background: #ffffff;
          border-radius: 10px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.13);
          padding: 14px 14px 10px 14px;
          animation: slideInToast 0.35s ease-out;
          border-left: 4px solid transparent;
          width: 100%;
          box-sizing: border-box;
        }
        .toast-inner {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }
        .toast-icon-wrap {
          flex-shrink: 0;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
        }
        .toast-body {
          flex: 1;
          min-width: 0;
        }
        .toast-header-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
        }
        .toast-title {
          font-size: 13px;
          font-weight: 700;
          color: #111827;
          margin: 0;
          line-height: 1.3;
        }
        .toast-close {
          flex-shrink: 0;
          background: none;
          border: none;
          cursor: pointer;
          color: #9ca3af;
          font-size: 14px;
          line-height: 1;
          padding: 0 2px;
          margin-top: -1px;
          transition: color 0.15s;
        }
        .toast-close:hover { color: #374151; }
        .toast-message {
          font-size: 12px;
          color: #4b5563;
          margin: 4px 0 0 0;
          line-height: 1.45;
          word-break: break-word;
        }
        .toast-sub {
          font-size: 11px;
          color: #6b7280;
          margin: 2px 0 0 0;
        }
        .toast-value {
          font-size: 11px;
          font-weight: 600;
          color: #16a34a;
          margin: 3px 0 0 0;
        }
        .toast-progress-track {
          margin-top: 10px;
          background: #e5e7eb;
          border-radius: 99px;
          height: 3px;
          overflow: hidden;
        }
        .toast-progress-bar {
          height: 3px;
          border-radius: 99px;
          width: 100%;
        }

        /* ── Mobile: stretch full width, stick to top ── */
        @media (max-width: 768px) {
          .toast-container {
            top: 12px !important;
            right: 10px !important;
            left: 10px !important;
            width: auto !important;
            max-width: 100% !important;
            gap: 8px !important;
          }
          .toast-item {
            padding: 12px 12px 8px 12px !important;
            border-radius: 9px !important;
            box-shadow: 0 3px 12px rgba(0,0,0,0.15) !important;
          }
          .toast-icon-wrap {
            width: 30px !important;
            height: 30px !important;
            font-size: 14px !important;
          }
          .toast-title {
            font-size: 13px !important;
          }
          .toast-message {
            font-size: 12px !important;
          }
          .toast-close {
            font-size: 15px !important;
            padding: 2px 4px !important;
          }
        }
      `}</style>

      <div className="toast-container">
        {notifications.map((notification) => {
          const color = getNotificationColor(notification);
          const icon  = getNotificationIcon(notification);
          const title = getNotificationTitle(notification);
          const borderColor = borderColorMap[color];
          const bgColor     = bgColorMap[color];
          const iconColor   = textColorMap[color];
          const barColor    = color === 'followup' ? '#8b5cf6' : color === 'green' ? '#22c55e' : color === 'red' ? '#ef4444' : '#3b82f6';
          const duration    = notification.type === 'followup' ? '3s' : '5s';

          return (
            <div
              key={notification.id}
              className="toast-item"
              style={{ borderLeftColor: borderColor }}
            >
              <div className="toast-inner">
                {/* Icon */}
                <div className="toast-icon-wrap" style={{ background: bgColor }}>
                  <span style={{ color: iconColor }}>{icon}</span>
                </div>

                {/* Body */}
                <div className="toast-body">
                  <div className="toast-header-row">
                    <p className="toast-title">{title}</p>
                    <button
                      className="toast-close"
                      onClick={() => onRemove(notification.id)}
                      aria-label="Dismiss"
                    >✕</button>
                  </div>

                  {notification.message ? (
                    <p className="toast-message">{notification.message}</p>
                  ) : notification.lead ? (
                    <>
                      <p className="toast-message">
                        <strong>{notification.lead.name}</strong>
                      </p>
                      <p className="toast-sub">
                        {notification.lead.company} · {notification.lead.source}
                      </p>
                      <p className="toast-value">
                        Value: ₹{notification.lead.value?.toLocaleString('en-IN')}
                      </p>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Progress bar */}
              <div className="toast-progress-track">
                <div
                  className="toast-progress-bar"
                  style={{
                    background: barColor,
                    animation: `shrink ${duration} linear forwards`
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default NotificationToast;
